import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './Payouts.scss';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useNavigate } from 'react-router';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import { useProtectedInterval } from '../../hooks/useProtectedInterval';
import Pagination from '../../components/Layout/Pagination';
import PageToolbar from '../../components/Layout/PageToolbar';
import Toast from '../../components/Toast/Toast';
import CopyButton from '../../components/CopyButton/CopyButton';
import { QrPayoutDetails, QrPayoutModal } from './QrPayout';
import RapiraPayoutActions from './RapiraPayoutActions';

Modal.setAppElement('#root');

export const CompletePayoutModal = ({ isOpen, onRequestClose, onComplete, payoutId, payoutPk, api_url, selectedPayout }) => {
    const { t } = useTranslation('payouts');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [receiptUploaded, setReceiptUploaded] = useState(false);
    const [uploadedReceiptUrl, setUploadedReceiptUrl] = useState(null);
    const [error, setError] = useState(null);

    // Загружаем чек и показываем preview
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        setError(null);

        if (selectedFile) {
            // Создаем preview для изображений
            if (selectedFile.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPreview(reader.result);
                };
                reader.readAsDataURL(selectedFile);
            } else if (selectedFile.type === 'application/pdf') {
                setPreview('pdf');
            }
        }
    };

    // Удаляем загруженный файл
    const handleRemoveFile = () => {
        setFile(null);
        setPreview(null);
        setError(null);
    };

    // Загружаем чек на сервер
    const uploadReceipt = async () => {
        if (!file) {
            setError(t('payouts:completeModal.selectFile'));
            return;
        }

        setUploading(true);
        setError(null);

        const jwtToken = localStorage.getItem('accessToken');
        const formData = new FormData();
        formData.append('receipt_file', file);

        try {
            const response = await fetch(`${api_url}providers/fiatpayoutrequest/${payoutPk}/upload_receipt/`, {
                method: 'POST',
                headers: {
                    'Authorization': `JWT ${jwtToken}`,
                },
                body: formData
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.text || t('payouts:completeModal.uploadError'));
            }

            setReceiptUploaded(true);
            setUploadedReceiptUrl(data.receipt_url);
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    // Закрытие модального окна
    const handleClose = () => {
        setFile(null);
        setPreview(null);
        setReceiptUploaded(false);
        setUploadedReceiptUrl(null);
        setError(null);
        onRequestClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel={t('payouts:completeModal.title')}
            className="confirmation-modal large-modal"
            overlayClassName="confirmation-modal-overlay"
        >
            <div className="confirmation-modal-header">
                <h2 className="confirmation-modal-title">{t('payouts:completeModal.titleWithId', { id: payoutId })}</h2>
                <button className="confirmation-modal-close" onClick={handleClose}>×</button>
            </div>
            <div className="confirmation-modal-content">
                {selectedPayout && (
                    <div style={{
                        marginBottom: '20px',
                        padding: '15px',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                    }}>
                        <h3 style={{marginTop: 0, marginBottom: '15px', fontSize: '16px', color: 'var(--text-primary)'}}>
                            📋 {t('payouts:completeModal.requestInfo')}
                        </h3>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '12px', color: 'var(--text-primary)'}}>
                            {(selectedPayout.fiat_amount?.value || selectedPayout.amount_info_display?.value) && (
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    <div>
                                        <strong>💰 {t('payouts:completeModal.amount')}</strong>{' '}
                                        <span style={{fontSize: '18px', color: '#2196F3', fontWeight: 'bold'}}>
                                            {selectedPayout.fiat_amount?.value
                                                ? `${selectedPayout.fiat_amount.value} ${selectedPayout.currency?.value || ''}`
                                                : selectedPayout.amount_info_display?.value.replace(/<[^>]*>/g, '').trim()
                                            }
                                        </span>
                                    </div>
                                    <CopyButton text={
                                        selectedPayout.fiat_amount?.value
                                            ? selectedPayout.fiat_amount.value
                                            : selectedPayout.amount_info_display?.value.replace(/<[^>]*>/g, '').trim().split(' ')[0]
                                    } />
                                </div>
                            )}
                            {selectedPayout.card_number?.value && (
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    <div>
                                        <strong>💳 {t('payouts:completeModal.cardNumber')}</strong>{' '}
                                        <span style={{fontFamily: 'monospace', fontSize: '16px'}}>
                                            {selectedPayout.card_number.value}
                                        </span>
                                    </div>
                                    <CopyButton text={selectedPayout.card_number.value} />
                                </div>
                            )}
                            {selectedPayout.bank_name?.value && (
                                <div>
                                    <strong>🏦 {t('payouts:completeModal.bank')}</strong> {selectedPayout.bank_name.value}
                                </div>
                            )}
                            {selectedPayout.cardholder_name?.value && (
                                <div>
                                    <strong>👤 {t('payouts:completeModal.cardholder')}</strong> {selectedPayout.cardholder_name.value}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {!receiptUploaded ? (
                    <>
                        <p>{t('payouts:completeModal.uploadReceipt')}</p>
                        <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={handleFileChange}
                            style={{margin: '20px 0'}}
                        />
                        {preview && preview !== 'pdf' && (
                            <div style={{marginTop: '20px', textAlign: 'center'}}>
                                <div style={{position: 'relative', display: 'inline-block'}}>
                                    <img
                                        src={preview}
                                        alt="Preview"
                                        style={{maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)'}}
                                    />
                                    <button
                                        onClick={handleRemoveFile}
                                        style={{
                                            position: 'absolute',
                                            top: '10px',
                                            right: '10px',
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            border: 'none',
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#c82333';
                                            e.target.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = '#dc3545';
                                            e.target.style.transform = 'scale(1)';
                                        }}
                                        title={t('payouts:completeModal.removeImage')}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        )}
                        {preview === 'pdf' && (
                            <div style={{marginTop: '20px', textAlign: 'center'}}>
                                <div style={{position: 'relative', display: 'inline-block'}}>
                                    <div style={{padding: '40px', background: 'var(--bg-secondary)', borderRadius: '8px'}}>
                                        <p style={{fontSize: '48px', margin: '0'}}>📄</p>
                                        <p>{t('payouts:completeModal.pdfSelected')}</p>
                                    </div>
                                    <button
                                        onClick={handleRemoveFile}
                                        style={{
                                            position: 'absolute',
                                            top: '10px',
                                            right: '10px',
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            border: 'none',
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = '#c82333';
                                            e.target.style.transform = 'scale(1.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = '#dc3545';
                                            e.target.style.transform = 'scale(1)';
                                        }}
                                        title={t('payouts:completeModal.removeFile')}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        )}
                        {error && <p style={{color: 'red', marginTop: '10px'}}>{error}</p>}
                    </>
                ) : (
                    <>
                        <div style={{padding: '20px', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', marginBottom: '20px'}}>
                            <p style={{color: 'var(--success)', margin: '0'}}>✓ {t('payouts:completeModal.receiptUploaded')}</p>
                        </div>
                        {uploadedReceiptUrl && (
                            <div style={{marginTop: '20px', textAlign: 'center'}}>
                                {uploadedReceiptUrl.endsWith('.pdf') ? (
                                    <div style={{padding: '40px', background: 'var(--bg-secondary)', borderRadius: '8px'}}>
                                        <p style={{fontSize: '48px', margin: '0'}}>📄</p>
                                        <p>{t('payouts:completeModal.pdfUploaded')}</p>
                                        <a href={uploadedReceiptUrl} target="_blank" rel="noreferrer">{t('payouts:completeModal.openPdf')}</a>
                                    </div>
                                ) : (
                                    <img
                                        src={uploadedReceiptUrl}
                                        alt={t('payouts:completeModal.uploadedReceipt')}
                                        style={{maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)'}}
                                    />
                                )}
                            </div>
                        )}
                        <p style={{marginTop: '20px'}}>{t('payouts:completeModal.confirmComplete')}</p>
                    </>
                )}
            </div>
            <div className="confirmation-modal-actions">
                {!receiptUploaded ? (
                    <>
                        <button
                            className="confirmation-modal-button confirm"
                            onClick={uploadReceipt}
                            disabled={!file || uploading}
                        >
                            {uploading ? t('payouts:completeModal.uploading') : t('payouts:completeModal.uploadBtn')}
                        </button>
                        <button className="confirmation-modal-button cancel" onClick={handleClose}>{t('common:cancel')}</button>
                    </>
                ) : (
                    <>
                        <button className="confirmation-modal-button confirm" onClick={onComplete}>
                            {t('payouts:completeModal.completeBtn')}
                        </button>
                        <button className="confirmation-modal-button cancel" onClick={handleClose}>{t('common:cancel')}</button>
                    </>
                )}
            </div>
        </Modal>
    );
};

const ConfirmationModal = ({ isOpen, onRequestClose, onConfirm, payoutId, textLabel, textAction, selectedPayout }) => {
    const { t } = useTranslation('payouts');
    const [reason, setReason] = useState('');

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            contentLabel={textLabel}
            className="confirmation-modal"
            overlayClassName="confirmation-modal-overlay"
        >
            <div className="confirmation-modal-header">
                <h2 className="confirmation-modal-title">{textLabel}</h2>
                <button className="confirmation-modal-close" onClick={onRequestClose}>×</button>
            </div>
            <div className="confirmation-modal-content">
                <p>{t('payouts:confirmModal.confirmText', { action: textAction, id: payoutId })}</p>

                {selectedPayout && (
                    <div style={{
                        marginTop: '20px',
                        marginBottom: '20px',
                        padding: '15px',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                    }}>
                        <h3 style={{marginTop: '0', marginBottom: '15px', color: 'var(--text-primary)', fontSize: '16px'}}>
                            📋 {t('payouts:completeModal.requestInfo')}
                        </h3>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '12px', color: 'var(--text-primary)'}}>
                            {(selectedPayout.fiat_amount?.value || selectedPayout.amount_info_display?.value) && (
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    <div>
                                        <strong>💰 {t('payouts:completeModal.amount')}</strong>{' '}
                                        <span style={{fontSize: '18px', color: '#2196F3', fontWeight: 'bold'}}>
                                            {selectedPayout.fiat_amount?.value
                                                ? `${selectedPayout.fiat_amount.value} ${selectedPayout.currency?.value || ''}`
                                                : selectedPayout.amount_info_display?.value.replace(/<[^>]*>/g, '').trim()
                                            }
                                        </span>
                                    </div>
                                    <CopyButton text={
                                        selectedPayout.fiat_amount?.value
                                            ? selectedPayout.fiat_amount.value
                                            : selectedPayout.amount_info_display?.value.replace(/<[^>]*>/g, '').trim().split(' ')[0]
                                    } />
                                </div>
                            )}
                            {selectedPayout.card_number?.value && (
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    <div>
                                        <strong>💳 {t('payouts:completeModal.cardNumber')}</strong>{' '}
                                        <span style={{fontFamily: 'monospace', fontSize: '16px'}}>
                                            {selectedPayout.card_number.value}
                                        </span>
                                    </div>
                                    <CopyButton text={selectedPayout.card_number.value} />
                                </div>
                            )}
                            {selectedPayout.bank_name?.value && (
                                <div>
                                    <strong>🏦 {t('payouts:completeModal.bank')}</strong> {selectedPayout.bank_name.value}
                                </div>
                            )}
                            {selectedPayout.cardholder_name?.value && (
                                <div>
                                    <strong>👤 {t('payouts:completeModal.cardholder')}</strong> {selectedPayout.cardholder_name.value}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {textAction === t('payouts:confirmModal.actionCancel') && (
                    <textarea
                        placeholder={t('payouts:confirmModal.cancelReason')}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        style={{
                            width: '100%',
                            minHeight: '100px',
                            marginTop: '10px',
                            padding: '10px',
                            background: 'var(--bg-secondary)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            fontSize: '14px',
                            resize: 'vertical',
                            outline: 'none',
                            boxSizing: 'border-box'
                        }}
                    />
                )}
            </div>
            <div className="confirmation-modal-actions">
                <button
                    className="confirmation-modal-button confirm"
                    onClick={() => onConfirm(reason)}
                    disabled={textAction === t('payouts:confirmModal.actionCancel') && !reason}
                >
                    {t('common:yes')}
                </button>
                <button className="confirmation-modal-button cancel" onClick={onRequestClose}>{t('common:no')}</button>
            </div>
        </Modal>
    );
};

const RejectConfirmationModal = ({ isOpen, onRequestClose, onReject, payoutId, selectedPayout, rejectingPayout }) => {
    const { t } = useTranslation('payouts');
    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            contentLabel={t('payouts:rejectModal.title')}
            className="confirmation-modal"
            overlayClassName="confirmation-modal-overlay"
        >
            <div className="confirmation-modal-header">
                <h2 className="confirmation-modal-title">{t('payouts:rejectModal.title')}</h2>
                <button className="confirmation-modal-close" onClick={onRequestClose}>×</button>
            </div>
            <div className="confirmation-modal-content">
                <p style={{marginBottom: '15px'}}>
                    {t('payouts:rejectModal.confirmText', { id: payoutId })}
                </p>
                <div className="confirmation-modal-warning" style={{marginBottom: '20px'}}>
                    ⚠️ {t('payouts:rejectModal.warning')}
                </div>

                {selectedPayout && (
                    <div style={{
                        marginTop: '20px',
                        marginBottom: '20px',
                        padding: '15px',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                    }}>
                        <h3 style={{marginTop: '0', marginBottom: '15px', color: 'var(--text-primary)', fontSize: '16px'}}>
                            📋 {t('payouts:completeModal.requestInfo')}
                        </h3>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '12px', color: 'var(--text-primary)'}}>
                            {(selectedPayout.fiat_amount?.value || selectedPayout.amount_info_display?.value) && (
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    <div>
                                        <strong>💰 {t('payouts:completeModal.amount')}</strong>{' '}
                                        <span style={{fontSize: '18px', color: '#2196F3', fontWeight: 'bold'}}>
                                            {selectedPayout.fiat_amount?.value
                                                ? `${selectedPayout.fiat_amount.value} ${selectedPayout.currency?.value || ''}`
                                                : selectedPayout.amount_info_display?.value.replace(/<[^>]*>/g, '').trim()
                                            }
                                        </span>
                                    </div>
                                    <CopyButton text={
                                        selectedPayout.fiat_amount?.value
                                            ? selectedPayout.fiat_amount.value
                                            : selectedPayout.amount_info_display?.value.replace(/<[^>]*>/g, '').trim().split(' ')[0]
                                    } />
                                </div>
                            )}
                            {selectedPayout.card_number?.value && (
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    <div>
                                        <strong>💳 {t('payouts:completeModal.cardNumber')}</strong>{' '}
                                        <span style={{fontFamily: 'monospace', fontSize: '16px'}}>
                                            {selectedPayout.card_number.value}
                                        </span>
                                    </div>
                                    <CopyButton text={selectedPayout.card_number.value} />
                                </div>
                            )}
                            {selectedPayout.bank_name?.value && (
                                <div>
                                    <strong>🏦 {t('payouts:completeModal.bank')}</strong> {selectedPayout.bank_name.value}
                                </div>
                            )}
                            {selectedPayout.cardholder_name?.value && (
                                <div>
                                    <strong>👤 {t('payouts:completeModal.cardholder')}</strong> {selectedPayout.cardholder_name.value}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className="confirmation-modal-actions">
                <button
                    className="confirmation-modal-button confirm"
                    onClick={onReject}
                    disabled={rejectingPayout}
                >
                    {rejectingPayout ? t('payouts:rejectModal.processing') : t('payouts:rejectModal.confirmBtn')}
                </button>
                <button
                    className="confirmation-modal-button cancel"
                    onClick={onRequestClose}
                    disabled={rejectingPayout}
                >
                    {t('common:cancel')}
                </button>
            </div>
        </Modal>
    );
};

// Утилита: извлекает текст из HTML строки
const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};

// Парсинг суммы из HTML "<strong>2294.0</strong> RUB"
const parseAmount = (html) => {
    if (!html) return { amount: '', currency: '' };
    const text = stripHtml(html);
    const match = text.match(/^([\d\s.,]+)\s*(\w+)$/);
    if (match) return { amount: match[1].trim(), currency: match[2] };
    return { amount: text, currency: '' };
};

// Копирование в буфер обмена
// Маппинг статусов на визуальные стили
const PAYOUT_STATUS_CONFIG = {
    assigned:   { label: 'Назначена',  bg: '#3b82f6',                textColor: '#ffffff', rowBg: 'rgba(59,130,246,0.04)' },
    completed:  { label: 'Выполнена',  bg: '#22c55e',                textColor: '#ffffff', rowBg: 'rgba(34,197,94,0.04)' },
    cancelled:  { label: 'Отменена',   bg: 'rgba(107,114,128,0.15)', textColor: '#6b7280', rowBg: 'rgba(107,114,128,0.04)' },
    canceled:   { label: 'Отменена',   bg: 'rgba(107,114,128,0.15)', textColor: '#6b7280', rowBg: 'rgba(107,114,128,0.04)' },
    rejected:   { label: 'Отказ',      bg: '#f59e0b',                textColor: '#ffffff', rowBg: 'rgba(245,158,11,0.04)' },
    refunded:   { label: 'Возврат',    bg: '#f59e0b',                textColor: '#ffffff', rowBg: 'rgba(245,158,11,0.04)' },
    pending:    { label: 'Ожидает',    bg: 'rgba(139,92,246,0.15)',  textColor: '#8b5cf6', rowBg: 'rgba(139,92,246,0.04)' },
    processing: { label: 'Обработка',  bg: 'rgba(6,182,212,0.15)',   textColor: '#06b6d4', rowBg: 'rgba(6,182,212,0.04)' },
    new:        { label: 'Новая',      bg: 'rgba(245,158,11,0.15)',  textColor: '#f59e0b', rowBg: 'rgba(245,158,11,0.04)' },
};

// Парсинг реквизитов выплаты из HTML
const parsePayoutDetails = (html) => {
    if (!html) return { cardNumber: '', bankName: '', cardholderName: '' };
    const parts = html.split(/<br\s*\/?>/i);
    return {
        cardNumber: stripHtml(parts[0] || '').replace(/[^\d\s]/g, '').trim(),
        bankName: stripHtml(parts[1] || '').trim(),
        cardholderName: stripHtml(parts[2] || '').trim(),
    };
};

// Парсинг USDT суммы из "27.77 USDT"
const parseUsdtAmount = (html) => {
    if (!html) return { amount: '', currency: '' };
    const text = stripHtml(html);
    const match = text.match(/^([\d\s.,]+)\s*(\w+)$/);
    if (match) return { amount: match[1].trim(), currency: match[2] };
    return { amount: text, currency: '' };
};

// Статусы для отображения (вынесены за пределы компонента для оптимизации)
const statusesDefault = {
    "new": "Новая",
    "assigned": "Назначена",
    "completed": "Выполнена",
    "canceled": "Отменена",
    "refunded": "Возврат"
};

export default function Payouts() {
    const { t } = useTranslation('payouts');
    const theme = useSelector((state) => state.APP_THEME);
    const api_url = useSelector((state) => state.API_URL);
    const [columns, setColumns] = useState([]);
    const [modalComplete, setModalComplete] = useState(false);
    const [modalCancel, setModalCancel] = useState(false);
    const [modalReject, setModalReject] = useState(false);
    const [rejectingPayout, setRejectingPayout] = useState(false);
    const [selectedPayoutId, setSelectedPayoutId] = useState('');
    const [selectedPayoutPk, setSelectedPayoutPk] = useState('');
    const [selectedPayout, setSelectedPayout] = useState(null);
    const [originalPayouts, setOriginalPayouts] = useState([]);
    const [modalSettings, setModalSettings] = useState(false);
    const [modalRules, setModalRules] = useState(false);
    const [payoutSettings, setPayoutSettings] = useState([]);

    const [searchNumber, setSearchNumber] = useState('');
    const [selectedButtons, setSelectedButtons] = useState([]);
    const [statusValues, setStatusValues] = useState([]);
    const [methodFilter, setMethodFilter] = useState('');

    const [errorFetching, setErrorFetching] = useState(false);
    const [errorAction, setErrorAction] = useState(false);
    const [actionSuccess, setActionSuccess] = useState(false);
    const [actionText, setActionText] = useState('');

    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(1);
    const [rowsCount, setRowsCount] = useState(0);

    const handleButtonClick = (button) => {
        const buttonValuesMap = {
            [t('payouts:filters.assigned')]: ['assigned'],
            [t('payouts:filters.completed')]: ['completed'],
            [t('payouts:filters.canceled')]: ['canceled', 'refunded']
        };

        const buttonValues = buttonValuesMap[button];

        if (selectedButtons.includes(button)) {
            setSelectedButtons(selectedButtons.filter((item) => item !== button));
            setStatusValues(statusValues.filter((item) => !buttonValues.includes(item)));
        } else {
            setSelectedButtons([...selectedButtons, button]);
            setStatusValues([...statusValues, ...buttonValues]);
        }
    };

    const navigate = useNavigate();

    const generateColumns = useCallback((data) => {
        if (data.length > 0) {
            const firstItem = data[0];
            const generatedColumns = Object.keys(firstItem).slice(1).filter(key => !key.startsWith('_')).map(key => ({
                id: key,
                label: firstItem[key]?.label || key,
                minWidth: 170,
                align: 'left',
                format: (value) => <div>{stripHtml(String(value || ''))}</div>,
                formatStatus: (value) => <div className={`statusCell ${value}`}>{statusesDefault[value]}</div>,
            }));
            setColumns(generatedColumns);
        }
    }, []);

    useEffect(() => {
        const jwtToken = localStorage.getItem('accessToken');
        if (!jwtToken) {
            navigate('/auth');
            localStorage.clear();
        }
    }, [navigate]);

    const [rows, setRows] = useState([]);

    const createDataFromPayout = useCallback((payout) => {
        let data = {};
        for (let key in payout) {
            if (key === 'pk') {
                data[key] = payout[key];
            } else {
                data[key] = payout[key]?.value;
            }
        }
        return data;
    }, []);

    const getPayoutsData = useCallback(async () => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const statusFilter = statusValues.length > 0 ? `&status__in=${statusValues.join(',')}` : '';
            const response = await fetch(
                `${api_url}providers/fiatpayoutrequest/info/?page=${pageIndex}&page_size=${pageSize}${statusFilter}${methodFilter ? `&payout_method=${methodFilter}` : ''}&search=${encodeURIComponent(searchNumber)}`,
                {
                    headers: {
                        'Authorization': `JWT ${jwtToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.clear();
                    navigate('/auth');
                }
                throw new Error('Network error');
            }

            const data = await response.json();
            setOriginalPayouts(data.results); // Сохраняем оригинальные данные
            setRows(data.results.map(createDataFromPayout));
            setRowsCount(data.count);
            generateColumns(data.results);
        } catch (error) {
            // console.error('Error fetching payouts:', error);
            setErrorFetching(true);
        }
    }, [pageIndex, pageSize, statusValues, methodFilter, searchNumber, api_url, navigate, generateColumns, createDataFromPayout]);

    useProtectedInterval(getPayoutsData, [pageIndex, pageSize, statusValues, methodFilter, searchNumber, modalComplete, modalCancel, modalReject]);

    function completePayout() {
        const jwtToken = localStorage.getItem('accessToken');
        fetch(`${api_url}providers/fiatpayoutrequest/${selectedPayoutPk}/complete_payout/`, {
            method: 'POST',
            headers: {
                'Authorization': `JWT ${jwtToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            if (!response.ok) {
                setErrorAction(true);
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            setActionSuccess(true);
            setActionText(data.text || t('payouts:errors.completeSuccess'));
        })
        .catch(error => {
            // console.error('Error completing payout:', error);
            setActionText(t('payouts:errors.completeError'));
        })
        .finally(() => {
            setModalComplete(false);
        });
    }

    function cancelPayout(reason) {
        const jwtToken = localStorage.getItem('accessToken');
        fetch(`${api_url}providers/fiatpayoutrequest/${selectedPayoutPk}/cancel_payout/`, {
            method: 'POST',
            headers: {
                'Authorization': `JWT ${jwtToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ reason })
        })
        .then(response => {
            if (!response.ok) {
                setErrorAction(true);
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            setActionSuccess(true);
            setActionText(data.text || t('payouts:errors.cancelSuccess'));
        })
        .catch(error => {
            // console.error('Error canceling payout:', error);
            setActionText(t('payouts:errors.cancelError'));
        })
        .finally(() => {
            setModalCancel(false);
        });
    }

    function rejectPayout() {
        if (rejectingPayout) return; // Защита от двойного клика

        setRejectingPayout(true);
        const jwtToken = localStorage.getItem('accessToken');
        fetch(`${api_url}providers/fiatpayoutrequest/${selectedPayoutPk}/reject_payout/`, {
            method: 'POST',
            headers: {
                'Authorization': `JWT ${jwtToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            if (!response.ok) {
                setErrorAction(true);
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
            setActionSuccess(true);
            setActionText(data.text || t('payouts:errors.rejectSuccess'));
        })
        .catch(error => {
            // console.error('Error rejecting payout:', error);
            setActionText(t('payouts:errors.rejectError'));
        })
        .finally(() => {
            setModalReject(false);
            setRejectingPayout(false);
        });
    }

    const handleClearFilters = () => {
        setSearchNumber('');
        setMethodFilter('');
        setPageIndex(1);
        setSelectedButtons([]);
        setStatusValues([]);
    };

    const loadPayoutSettings = async () => {
        const jwtToken = localStorage.getItem('accessToken');
        try {
            const response = await fetch(
                `${api_url}accounts/operatorpayoutsettings/`,
                {
                    headers: {
                        'Authorization': `JWT ${jwtToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.clear();
                    navigate('/auth');
                }
                throw new Error('Network error');
            }

            const data = await response.json();
            // Проверяем является ли ответ пагинированным (с полем results) или просто массивом
            if (data.results && Array.isArray(data.results)) {
                setPayoutSettings(data.results);
            } else if (Array.isArray(data)) {
                setPayoutSettings(data);
            } else {
                // Если data не массив и не имеет results, устанавливаем пустой массив
                // console.error('Unexpected data format:', data);
                setPayoutSettings([]);
            }
        } catch (error) {
            // console.error('Error fetching payout settings:', error);
            setPayoutSettings([]);
        }
    };


    return (
        <div className={theme === 'light' ? "Payouts" : "Payouts dark"}>
            <Toast show={errorFetching} type="error" message={t('payouts:errors.fetchError')} onClose={() => setErrorFetching(false)} />
            <Toast show={errorAction} type="error" message={actionText} onClose={() => setErrorAction(false)} />
            <Toast show={actionSuccess} type="success" message={actionText} onClose={() => setActionSuccess(false)} />

            <div className="content">
                <PageToolbar
                    filters={[
                        {label: t('payouts:filters.assigned'), value: t('payouts:filters.assigned')},
                        {label: t('payouts:filters.completed'), value: t('payouts:filters.completed')},
                        {label: t('payouts:filters.canceled'), value: t('payouts:filters.canceled')}
                    ]}
                    onFilterChange={handleButtonClick}
                    activeFilters={selectedButtons}
                    searchValue={searchNumber}
                    onSearchChange={setSearchNumber}
                    searchPlaceholder={t('payouts:searchPlaceholder')}
                    onClearAll={handleClearFilters}
                    extraButtons={
                        <>
                            {['', 'card', 'qr'].map(method => (
                                <button key={method} aria-pressed={methodFilter === method} className={methodFilter === method ? 'active' : ''} onClick={() => { setMethodFilter(method); setPageIndex(1); }}>
                                    {t(`payouts:qr.filter.${method || 'all'}`)}
                                </button>
                            ))}
                            <button onClick={() => { setModalSettings(true); loadPayoutSettings(); }}>
                                {t('payouts:settingsBtn')}
                            </button>
                            <button className="active" onClick={() => setModalRules(true)}>
                                {t('payouts:rulesBtn')}
                            </button>
                        </>
                    }
                />
                <Pagination
                    pageIndex={pageIndex}
                    totalCount={rowsCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[5, 10, 15]}
                />
                <Paper className="payout-table-paper" sx={{ width: '100%', overflow: 'hidden', background: `${theme === 'light' ? 'white' : 'rgba(31, 38, 48, 1)'}`, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    <TableContainer sx={{ maxHeight: "auto" }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        column.id !== 'status' &&
                                        column.id !== 'card_number' &&
                                        column.id !== 'bank_name' &&
                                        column.id !== 'cardholder_name' && (
                                            <TableCell
                                                key={column.id}
                                                align={column.align}
                                                className="payout-table-head"
                                                style={theme === 'light'
                                                    ? {color: "var(--text-secondary)", background: "#f1f3f5", fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', minWidth: column.minWidth}
                                                    : {color: "var(--text-secondary)", background: "rgb(22, 29, 38)", fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', minWidth: column.minWidth}
                                                }
                                            >
                                                {column.label}
                                            </TableCell>
                                        )
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, index) => {
                                    const isAssigned = row.status === 'assigned';
                                    const meta = row._payout_meta || {};
                                    const isQr = meta.method === 'qr';
                                    const statusConf = PAYOUT_STATUS_CONFIG[row.status] || {};
                                    const rowStyle = statusConf.rowBg ? { background: statusConf.rowBg } : {};
                                    return (
                                        <TableRow hover tabIndex={-1} key={row.uid || index} style={rowStyle} className={`payout-row status-${row.status}`}>
                                            {columns.map((column) => {
                                                // Скрываем служебные колонки
                                                if (column.id === 'status' ||
                                                    column.id === 'card_number' ||
                                                    column.id === 'bank_name' ||
                                                    column.id === 'cardholder_name') {
                                                    return null;
                                                }

                                                const value = row[column.id];
                                                const cellStyle = theme === 'light' ? {color: "var(--text-primary)"} : {color: "var(--text-primary)"};

                                                // Номер заявки — моноширный + копирование
                                                if (column.id === 'uid') {
                                                    return (
                                                        <TableCell key={column.id} style={cellStyle}>
                                                            <div className="payout-cell-id">
                                                                <span className="payout-id-number">{value}</span>
                                                                <CopyButton text={String(value)} />
                                                            </div>
                                                        </TableCell>
                                                    );
                                                }

                                                // Сумма — крупно и жирно
                                                if (column.id === 'amount_info_display') {
                                                    const { amount, currency } = parseAmount(value);
                                                    return (
                                                        <TableCell key={column.id} style={cellStyle}>
                                                            <div className="payout-cell-amount">
                                                                <span className="payout-amount-value">{amount}</span>
                                                                <span className="payout-amount-currency">{currency}</span>
                                                            </div>
                                                        </TableCell>
                                                    );
                                                }

                                                // Реквизиты выплаты — структурированный блок
                                                if (column.id === 'payout_details_display') {
                                                    if (isQr) return (
                                                        <TableCell key={column.id} style={cellStyle}>
                                                            <QrPayoutDetails meta={meta} apiUrl={api_url} payoutPk={row.pk} />
                                                        </TableCell>
                                                    );
                                                    const details = parsePayoutDetails(value);
                                                    return (
                                                        <TableCell key={column.id} style={cellStyle}>
                                                            <div className="payout-cell-details">
                                                                <span className="payout-details-card">{details.cardNumber}</span>
                                                                <span className="payout-details-bank">{details.bankName}</span>
                                                                <span className="payout-details-holder">{details.cardholderName}</span>
                                                            </div>
                                                        </TableCell>
                                                    );
                                                }

                                                // Статус — цветной badge с заливкой
                                                if (column.id === 'status_display') {
                                                    return (
                                                        <TableCell key={column.id} style={cellStyle}>
                                                            <span
                                                                className={`payout-status-badge status-${row.status}`}
                                                                style={{
                                                                    background: statusConf.bg || '#e5e7eb',
                                                                    color: statusConf.textColor || '#374151',
                                                                }}
                                                            >
                                                                {t('payouts:statuses.' + row.status, { defaultValue: statusConf.label || row.status })}
                                                            </span>
                                                        </TableCell>
                                                    );
                                                }

                                                // Сумма USDT — жирно + валюта мелким
                                                if (column.id === 'operator_usdt_display') {
                                                    const { amount: usdtAmount, currency: usdtCurrency } = parseUsdtAmount(value);
                                                    return (
                                                        <TableCell key={column.id} style={cellStyle}>
                                                            <div className="payout-cell-amount">
                                                                {isQr && <span>{t('payouts:qr.salary')}</span>}
                                                                <span className="payout-amount-value">{usdtAmount}</span>
                                                                <span className="payout-amount-currency">{usdtCurrency}</span>
                                                            </div>
                                                        </TableCell>
                                                    );
                                                }

                                                // Даты — компактные
                                                if (column.id === 'assigned_at_formatted' || column.id === 'completed_at_formatted') {
                                                    const text = stripHtml(value);
                                                    const dateParts = text && text !== '-' ? text.split(' ') : null;
                                                    return (
                                                        <TableCell key={column.id} style={cellStyle}>
                                                            {dateParts ? (
                                                                <div className="payout-cell-date">
                                                                    <span className="payout-date-value">{dateParts[0]}</span>
                                                                    <span className="payout-date-time">{dateParts[1] || ''}</span>
                                                                </div>
                                                            ) : (
                                                                <span className="payout-done-label">--</span>
                                                            )}
                                                        </TableCell>
                                                    );
                                                }

                                                // Действия — кнопки
                                                if (column.id === 'operator_actions_display') {
                                                    return (
                                                        <TableCell key={column.id} style={cellStyle}>
                                                            {meta.can_view_rapira && <RapiraPayoutActions apiUrl={api_url} payoutPk={row.pk} hasDeal={meta.has_rapira_deal} />}
                                                            {isAssigned && meta.can_submit !== false ? (
                                                                <div className="payout-actions">
                                                                    <button
                                                                        className='payout-action-btn action-complete'
                                                                        onClick={() => {
                                                                            const originalPayout = originalPayouts.find(p => p.pk === row.pk);
                                                                            setModalComplete(true);
                                                                            setSelectedPayoutId(row.uid);
                                                                            setSelectedPayoutPk(row.pk);
                                                                            setSelectedPayout(originalPayout);
                                                                        }}
                                                                    >
                                                                        {isQr ? t('payouts:qr.uploadTitle') : t('payouts:actions.complete')}
                                                                    </button>
                                                                    <button
                                                                        className='payout-action-btn action-reject'
                                                                        onClick={() => {
                                                                            const originalPayout = originalPayouts.find(p => p.pk === row.pk);
                                                                            setModalReject(true);
                                                                            setSelectedPayoutId(row.uid);
                                                                            setSelectedPayoutPk(row.pk);
                                                                            setSelectedPayout(originalPayout);
                                                                        }}
                                                                        disabled={rejectingPayout}
                                                                    >
                                                                        {t('payouts:actions.reject')}
                                                                    </button>
                                                                    {!isQr && <button
                                                                        className='payout-action-btn action-cancel'
                                                                        onClick={() => {
                                                                            const originalPayout = originalPayouts.find(p => p.pk === row.pk);
                                                                            setModalCancel(true);
                                                                            setSelectedPayoutId(row.uid);
                                                                            setSelectedPayoutPk(row.pk);
                                                                            setSelectedPayout(originalPayout);
                                                                        }}
                                                                    >
                                                                        {t('payouts:actions.cancel')}
                                                                    </button>}
                                                                </div>
                                                            ) : (
                                                                <span className="payout-done-label">--</span>
                                                            )}
                                                        </TableCell>
                                                    );
                                                }

                                                // Прочие колонки — fallback
                                                return (
                                                    <TableCell key={column.id} align={column.align} style={cellStyle}>
                                                        {column.format ? <span>{stripHtml(value)}</span> : value}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                <Pagination
                    pageIndex={pageIndex}
                    totalCount={rowsCount}
                    pageSize={pageSize}
                    onPageChange={setPageIndex}
                    onPageSizeChange={setPageSize}
                    pageSizes={[5, 10, 15]}
                />
                <CompletePayoutModal
                    isOpen={modalComplete && selectedPayout?._payout_meta?.value?.method !== 'qr'}
                    onRequestClose={() => setModalComplete(false)}
                    onComplete={completePayout}
                    payoutId={selectedPayoutId}
                    payoutPk={selectedPayoutPk}
                    api_url={api_url}
                    selectedPayout={selectedPayout}
                />
                {modalComplete && selectedPayout?._payout_meta?.value?.method === 'qr' && (
                    <QrPayoutModal
                        apiUrl={api_url}
                        payoutPk={selectedPayoutPk}
                        payoutId={selectedPayoutId}
                        meta={selectedPayout._payout_meta.value}
                        onClose={() => setModalComplete(false)}
                        onCompleted={() => {
                            setModalComplete(false);
                            setActionText(t('payouts:errors.completeSuccess'));
                            setActionSuccess(true);
                            getPayoutsData();
                        }}
                    />
                )}
                <ConfirmationModal
                    isOpen={modalCancel}
                    onRequestClose={() => setModalCancel(false)}
                    onConfirm={cancelPayout}
                    payoutId={selectedPayoutId}
                    textLabel={t('payouts:confirmModal.confirmCancel')}
                    textAction={t('payouts:confirmModal.actionCancel')}
                    selectedPayout={selectedPayout}
                />
                <RejectConfirmationModal
                    isOpen={modalReject}
                    onRequestClose={() => setModalReject(false)}
                    onReject={rejectPayout}
                    payoutId={selectedPayoutId}
                    selectedPayout={selectedPayout}
                    rejectingPayout={rejectingPayout}
                />
                <Modal
                    isOpen={modalSettings}
                    onRequestClose={() => setModalSettings(false)}
                    contentLabel={t('payouts:settingsModal.title')}
                    className="confirmation-modal settings-modal"
                    overlayClassName="confirmation-modal-overlay"
                >
                    <h2 className="confirmation-modal-title">{t('payouts:settingsModal.title')}</h2>
                    <div className="confirmation-modal-content" style={{minWidth: '450px'}}>
                        <p style={{marginBottom: '16px', color: theme === 'light' ? '#666' : '#b0b0b0'}}>
                            {t('payouts:settingsModal.subtitle')}
                        </p>
                        <p>{t('payouts:qr.settingsHelp')}</p>
                        {payoutSettings.length === 0 ? (
                            <p>{t('payouts:settingsModal.loading')}</p>
                        ) : (
                            <>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px'}}>
                                    {payoutSettings.map((setting) => {
                                        const hasActiveCreds = setting.has_active_credentials || false;
                                        const activeCredsCount = setting.active_credentials_count || 0;

                                        return (
                                            <div
                                                key={setting.id}
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '12px',
                                                    border: theme === 'light' ? '1px solid #ddd' : '1px solid #444',
                                                    borderRadius: '8px',
                                                    backgroundColor: hasActiveCreds
                                                        ? (theme === 'light' ? '#f0fff4' : '#1a3a1f')
                                                        : (theme === 'light' ? '#fff5f5' : '#3a1a1a')
                                                }}
                                            >
                                                <div>
                                                    <strong>{setting.currency?.code || 'N/A'}</strong>
                                                    {setting.qr_enabled && <div>{t('payouts:qr.enabled')}</div>}
                                                    <div style={{fontSize: '12px', color: theme === 'light' ? '#666' : '#b0b0b0'}}>
                                                        {t('payouts:settingsModal.commission')} {setting.fee}% | {t('payouts:settingsModal.priority')} {setting.priority}
                                                    </div>
                                                    <div style={{fontSize: '11px', marginTop: '4px', color: theme === 'light' ? '#888' : '#999'}}>
                                                        {t('payouts:settingsModal.activeRequisites')} {activeCredsCount}
                                                    </div>
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    fontSize: '14px',
                                                    fontWeight: '600'
                                                }}>
                                                    <span style={{
                                                        color: hasActiveCreds ? '#4CAF50' : '#f44336'
                                                    }}>
                                                        {hasActiveCreds ? '🟢 ' + t('payouts:settingsModal.active') : '🔴 ' + t('payouts:settingsModal.noRequisites')}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div style={{
                                    padding: '16px',
                                    backgroundColor: theme === 'light' ? '#e3f2fd' : '#1a2a3a',
                                    borderRadius: '8px',
                                    border: theme === 'light' ? '1px solid #2196F3' : '1px solid #1976D2'
                                }}>
                                    <div style={{
                                        fontSize: '14px',
                                        color: theme === 'light' ? '#1976D2' : '#64B5F6',
                                        marginBottom: '8px',
                                        fontWeight: '600'
                                    }}>
                                        💡 {t('payouts:settingsModal.infoTitle')}
                                    </div>
                                    <div style={{
                                        fontSize: '13px',
                                        color: theme === 'light' ? '#555' : '#ccc',
                                        lineHeight: '1.5'
                                    }}>
                                        <p style={{margin: '0 0 4px 0'}}>{t('payouts:settingsModal.infoText1')}</p>
                                        <p style={{margin: 0}}>{t('payouts:settingsModal.infoText2')}</p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="confirmation-modal-actions">
                        <button className="confirmation-modal-button confirm" onClick={() => setModalSettings(false)}>{t('common:close')}</button>
                    </div>
                </Modal>
                <Modal
                    isOpen={modalRules}
                    onRequestClose={() => setModalRules(false)}
                    contentLabel={t('payouts:rulesModal.title')}
                    className="confirmation-modal rules-modal"
                    overlayClassName="confirmation-modal-overlay"
                    style={{
                        content: {
                            maxWidth: '900px',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            backgroundColor: theme === 'light' ? '#ffffff' : '#2c2c2c'
                        }
                    }}
                >
                    <p>{t('payouts:qr.cardRulesNotice')}</p>
                    <div style={{
                        background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                        padding: '20px',
                        marginBottom: '20px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    }}>
                        <h2 style={{
                            margin: 0,
                            color: 'white',
                            fontSize: '24px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                        }}>
                            📋 {t('payouts:rulesModal.title')}
                        </h2>
                        <p style={{
                            margin: '8px 0 0 0',
                            color: 'white',
                            textAlign: 'center',
                            fontSize: '14px',
                            fontStyle: 'italic'
                        }}>
                            {t('payouts:rulesModal.subtitle')}
                        </p>
                    </div>
                    <div className="confirmation-modal-content" style={{padding: '0 20px'}}>
                        <div style={{
                            backgroundColor: theme === 'light' ? '#fff3e0' : '#4a3a2a',
                            border: '2px solid #ff9800',
                            borderRadius: '8px',
                            padding: '16px',
                            marginBottom: '20px'
                        }}>
                            <p style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: theme === 'light' ? '#e65100' : '#ffb74d',
                                textAlign: 'center'
                            }}>
                                ⚠️ {t('payouts:rulesModal.warning')}
                            </p>
                        </div>
                        <pre style={{
                            fontFamily: 'Monaco, "Courier New", monospace',
                            fontSize: '14px',
                            lineHeight: '1.8',
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            backgroundColor: theme === 'light' ? '#ffffff' : '#1a1a1a',
                            color: theme === 'light' ? '#1a1a1a' : '#e0e0e0',
                            padding: '24px',
                            borderRadius: '8px',
                            border: theme === 'light' ? '2px solid #e0e0e0' : '2px solid #404040',
                            maxHeight: '60vh',
                            overflow: 'auto',
                            fontWeight: '500'
                        }}>{`
⏱️  ОСНОВНЫЕ СРОКИ
────────────────────────────────────────────────────────────

  ⏰ Выполнить перевод: НЕ БОЛЕЕ 2 ЧАСОВ
  📸 Загрузить чек:     В ТЕЧЕНИЕ 1 ЧАСА после перевода


✅ ЧЕК-ЛИСТ ПЕРЕД ПЕРЕВОДОМ (ОБЯЗАТЕЛЬНО!)
────────────────────────────────────────────────────────────

  [ ] Скопировал номер карты кнопкой "Копировать" (НЕ вручную!)
  [ ] Скопировал сумму кнопкой "Копировать" (НЕ вручную!)
  [ ] Проверил банк получателя (совпадает с заявкой?)
  [ ] Проверил валюту (UAH/RUB/KZT — точно та?)
  [ ] Готов сделать скриншот чека СРАЗУ после перевода
  [ ] Понимаю что уложусь в 2 часа


🔴 ОШИБКА В БАНКЕ — ЭТО ВАША ВИНА!
────────────────────────────────────────────────────────────

  Ситуация: Перевели в неправильный банк

  ВЫ ОБЯЗАНЫ:
  ✅ Сделать ВЕРНЫЙ перевод в правильный банк
  ✅ Самостоятельно решить вопрос с ошибочным переводом

  ЕСЛИ ОТКАЖЕТЕСЬ:
  ❌ Refund заявки (провайдеру вернутся деньги)
  ❌ С вас спишут полную сумму
  ❌ Штраф
  ❌ Блокировка


🔴 ОШИБКА В СУММЕ — ЭТО ВАША ВИНА!
────────────────────────────────────────────────────────────

  Перевели БОЛЬШЕ:
  ✅ Вежливо попросить вернуть (через поддержку)
  ⚠️ Провайдер может отказать — это ВАШИ потери

  Перевели МЕНЬШЕ:
  ✅ ОБЯЗАНЫ доплатить недостающую сумму
  ❌ Отказ = Refund + штраф + блокировка


⚡ АГРЕССИЯ = ШТРАФ х2 + БАН НАВСЕГДА!
────────────────────────────────────────────────────────────

  НЕЛЬЗЯ:
  ❌ Оскорблять провайдера/поддержку
  ❌ Угрожать
  ❌ Грубить
  ❌ Шантажировать

  ПЕРВОЕ НАРУШЕНИЕ:  штраф х1 + предупреждение
  ПОВТОРНОЕ:         штраф х5 + БАН НАВСЕГДА!


💰 ШТРАФЫ
────────────────────────────────────────────────────────────

  Задержка 12-24 часа           →  10 USDT
  Невыполнение > 24 часов       →  50 USDT + блокировка 3 дня
  Ошибка в банке (не исправил)  →  х1 от заявки + Refund
  Ошибка в сумме (не исправил)  →  х1 от заявки + Refund
  Поддельный чек                →  х3 от заявки + БАН НАВСЕГДА
  Агрессия (первый раз)         →  х1 от заявки
  Агрессия (повторно)           →  х5 от заявки + БАН НАВСЕГДА


✅ ОТМЕНА ЗАЯВКИ — ТОЛЬКО ПО ПРИЧИНЕ!
────────────────────────────────────────────────────────────

  МОЖНО отменить:
  ✅ Неверные реквизиты (ошибка провайдера)
  ✅ Банк отклонил перевод
  ✅ Технические проблемы

  НЕЛЬЗЯ отменить:
  ❌ Передумал
  ❌ Занят
  ❌ Не хочу работать
  ❌ Нет времени


🔄 КНОПКА "ОТКАЗАТЬСЯ" — ДЛЯ ПЕРЕНАЗНАЧЕНИЯ
────────────────────────────────────────────────────────────

  КОГДА ИСПОЛЬЗОВАТЬ:
  ✅ Не можете выполнить перевод (личные обстоятельства)
  ✅ Нет доступа к нужному банку
  ✅ Технические проблемы с вашей стороны

  ЧТО ПРОИСХОДИТ:
  ➡️  Заявка автоматически передается другому оператору
  ➡️  Ваш отказ записывается в систему
  ➡️  При частых отказах — возможны санкции

  ВАЖНО:
  ⚠️ Это НЕ отмена заявки — она остается активной
  ⚠️ Используйте только если действительно не можете выполнить
  ⚠️ Злоупотребление = штрафы


📸 ТРЕБОВАНИЯ К ЧЕКУ
────────────────────────────────────────────────────────────

  ✅ Фото или PDF
  ✅ Текст читаем (четкое изображение)
  ✅ Видна сумма
  ✅ Видна дата и время
  ✅ Видны реквизиты получателя
  ✅ Статус "Успешно" / "Выполнено"
  ✅ Можно скриншот из банковского приложения


🚨 КРИТИЧЕСКИЕ ПРАВИЛА
────────────────────────────────────────────────────────────

  1. ВСЕГДА используйте кнопки "Копировать" (номер карты, сумма)
  2. ПРОВЕРЯЙТЕ сумму и банк ТРИ РАЗА перед переводом
  3. ДЕЛАЙТЕ скриншот чека СРАЗУ после перевода
  4. БУДЬТЕ ВЕЖЛИВЫ всегда (даже если провайдер агрессивен)
  5. ПРИ ОШИБКЕ — сразу пишите в поддержку (не скрывайте)
  6. НЕ ОПАЗДЫВАЙТЕ — 2 часа это максимум

⚠️  ЗАПОМНИТЕ НАВСЕГДА
────────────────────────────────────────────────────────────

  🔴 Ваша ошибка = Ваши потери
  🔴 Агрессия = Штраф от х2 + Бан
  🔴 2 часа — не больше!
  🔴 Всегда используйте "Копировать"
                        `}</pre>
                    </div>
                    <div className="confirmation-modal-actions" style={{
                        borderTop: '2px solid #ff9800',
                        paddingTop: '16px',
                        marginTop: '20px'
                    }}>
                        <button
                            className="confirmation-modal-button confirm"
                            onClick={() => setModalRules(false)}
                            style={{
                                backgroundColor: '#ff9800',
                                padding: '12px 32px',
                                fontSize: '16px',
                                fontWeight: 'bold'
                            }}
                        >
                            {t('payouts:rulesModal.closeBtn')}
                        </button>
                    </div>
                </Modal>
            </div>
        </div>
    );
}
